import * as CryptoJS from 'crypto-js';
import { MemberModel } from '../../member/member.model';
import { environment } from '../../../../environments/environment';
import { ListingModel } from '../../property/listing.model';
import { PropertyModel } from '../../property/property.model';

export class LinkModel {

  public label: string = null;
  public url: string = null;

  //advanced
  public hash: string = null;
  public salt: string = null;
  public algorithm: string = null;
  public dateFormat: string = null;

  //permissions
  public statuses: string[] = [];
  public roles: string[] = [];

  constructor(model?: Partial<LinkModel>) {
    if (model?.label) this.label = model.label;
    if (model?.url) this.url = model.url;
    if (model?.hash) this.hash = model.hash;
    if (model?.salt) this.salt = model.salt;
    if (model?.algorithm) this.algorithm = model.algorithm;
    if (model?.dateFormat) this.dateFormat = model.dateFormat;
    if (model?.statuses?.length) this.statuses = [ ...model.statuses ];
    if (model?.roles?.length) this.roles = [ ...model.roles ];
    if (this.url?.toLowerCase().includes('{memberkey}'))
      this.url = this.url.replace(new RegExp(/{memberKey}/, 'gi'), '{member.memberKey}');
    if (this.url?.toLowerCase().includes('{listingkey}'))
      this.url = this.url.replace(new RegExp(/{listingKey}/, 'gi'), '{listing.listingKey}');
    if (this.hash?.toLowerCase().includes('{memberkey}'))
      this.hash = this.hash.replace(new RegExp(/{memberKey}/, 'gi'), '{member.memberKey}');
    if (this.hash?.toLowerCase().includes('{listingkey}'))
      this.hash = this.hash.replace(new RegExp(/{listingKey}/, 'gi'), '{listing.listingKey}');
  }

  public canShow(member: MemberModel, listing: ListingModel | PropertyModel): boolean {
    if (
      //role check
      this.roles.length && !member?.memberRoles.find(r => this.roles.includes(r.roleName)) ||
      //status check
      this.statuses.length && !this.statuses.includes(listing?.mlsStatus || listing?.['MlsStatus'])
    ) return false;

    return true;
  }

  public getUrl(member: MemberModel, listing: ListingModel | PropertyModel): string {
    let url = this.url || '';

    url = this.injectMemberData(url, member);
    url = this.injectListingData(url, listing);
    url = this.injectHash(url, member, listing);
    url = this.injectToday(url);

    return url;
  }

  private buildHash(member: MemberModel, listing: ListingModel | PropertyModel): string {
    let hash = this.hash || '';

    hash = this.injectMemberData(hash, member);
    hash = this.injectListingData(hash, listing);
    hash = this.injectSalt(hash);
    hash = this.injectToday(hash);

    switch (this.algorithm) {
      case 'MD5': return CryptoJS.MD5(hash);
      case null: return hash;
      default: environment.error('[link.model.ts > buildHash()]: Unsupported Hash Algorithm', this.algorithm);
    }
    return hash;
  }

  private injectMemberData(string: string, member: MemberModel): string {
    for (let key in member)
      if (string?.toLowerCase().includes('{member.'+key.toLowerCase()+'}'))
        string = string.replace(new RegExp('{member.'+key+'}', 'gi'), member[key] || '');
    return string;
  }

  private injectListingData(string: string, listing: ListingModel | PropertyModel): string {
    for (let key in listing)
      if (string?.toLowerCase().includes('{listing.'+key.toLowerCase()+'}'))
        string = string.replace(new RegExp('{listing.'+key+'}', 'gi'), listing[key] || '');
    return string;
  }

  private injectHash(string: string, member: MemberModel, listing: ListingModel | PropertyModel) {
    if (string?.toLowerCase().includes('{hash}'))
      string = string.replace(/{hash}/i, this.buildHash(member, listing));
    return string;
  }

  private injectSalt(string: string): string {
    if (string?.toLowerCase().includes('{salt}'))
      string = string.replace(/{salt}/i, this.salt || '');
    return string;
  }

  private injectToday(string: string): string {
    if (string?.toLowerCase().includes('{today}'))
      string = string.replace(/{today}/i, this.formatToday() || '');
    return string;
  }

  private formatToday(): string {
    //didn't wanna import a full date formatter because they're
    //friggen huge and the app is already massive
    //this'll do for the most common formats
    let today = new Date();
    let year = ''+today.getFullYear();
    let month = ''+(today.getMonth()+1);
    if (month.length === 1) month = '0'+month;
    let day = ''+today.getDate();
    if (day.length === 1) day = '0'+day;
    switch (this.dateFormat) {
      case 'YYYY-MM-DD': return `${year}-${month}-${day}`;
      case 'YYYYMMDD': return `${year}${month}${day}`;
      case 'YYMMDD': return `${year.substring(2)}${month}${day}`;
      case 'YY-MM-DD': return `${year.substring(2)}-${month}-${day}`;
      default: environment.error('[link.model.ts > formatToday()]: Unsupported Date Format', this.dateFormat);
    }
    return `${year}-${month}-${day}`;
  }
}

export const LinkDateFormats = [
  'YYYY-MM-DD',
  'YYYYMMDD',
  'YY-MM-DD',
  'YYMMDD',
];

export const HashAlgorithms = [
  'MD5',
];
